// frontend/src/tabs/useTabs.js
//
// The single public hook for tab orchestration. Pages and the sidebar
// consume this; nothing else in the tabs/ module is part of the public
// surface (the others are internals: storage, reducer, contexts).
//
// API contract:
//
//   const {
//     tabs, activeId,
//     open, openTab, close, focus, closeAllExceptHome,
//     countOpen, setTitle, setOwnTitle, replaceOwnTab, reorderTabs, MAX_TABS,
//   } = useTabs();
//
// open(path, { forceNew }) → focuses the open tab at `path`, else creates one.
//   throws TabCapError when cap is reached and a new tab would be needed.
//
// openTab(path, opts) → open(), with the cap-reached toast instead of the
//   throw. Every opener outside the tab strip calls this.
//
// setOwnTitle(title) / replaceOwnTab(path) → act on the tab the calling page
//   is rendered in. Throw when called outside a tab: a caller bug.
//
// close(id) → ignored for the Home tab; otherwise removes and re-focuses
//   the left neighbor (browser convention).
//
// closeAllExceptHome() → returns the surviving tab's path. A caller that
//   replaces the document afterwards (the store switch) must load THAT
//   path, not the current url — see the comment on the callback.
//
// focus(id) → throws if id is not a currently open tab. That's a caller
//   bug, not user-recoverable.

import { useCallback, useContext, useEffect, useRef } from 'react';
import {
  MAX_TABS,
  TabCapError,
  TabIdContext,
  useTabsStateContext,
  useTabsDispatch,
} from './tabsContexts';
import {
  HOME_TAB_ID,
  saveTabsState,
  seedHomeTab,
  clearTabSlots,
} from './storage';
import { markClean } from './useDirtyTabs';
import { emitTabCapReached } from './capEvent';

export { MAX_TABS, TabCapError };

export function useTabs() {
  const { tabs, activeId } = useTabsStateContext();
  const dispatch = useTabsDispatch();

  // Mirror `tabs` through a ref so `open` can stay reference-stable AND
  // read the latest tab list at call time. React's useReducer runs the
  // reducer during the next render — too late for the reducer to throw
  // synchronously to a try/catch around dispatch(). So the cap check
  // lives HERE in the hook, synchronously, where the throw lands inside
  // the caller's try/catch as designed.
  //
  // The mirror is synced in an effect, not during render: writing a ref
  // mid-render is a React anti-pattern (flagged by react-hooks/refs).
  // Effects flush after commit and before any user event fires, and this
  // hook's effect is registered ahead of its consumers' effects (useTabs
  // is called at the top of TabStrip), so every callback — and TabStrip's
  // URL-sync effect — reads an up-to-date tab list.
  const tabsRef = useRef(tabs);
  useEffect(() => {
    tabsRef.current = tabs;
  }, [tabs]);

  const open = useCallback(
    (path, opts = {}) => {
      const { forceNew = false } = opts;
      const currentTabs = tabsRef.current;

      // Single-instance dedup: dispatch FOCUS, not OPEN. Keeps the
      // reducer pure ("OPEN = create a new tab", no branching).
      if (!forceNew) {
        const existing = currentTabs.find((t) => t.path === path);
        if (existing) {
          dispatch({ type: 'FOCUS', id: existing.id });
          return;
        }
      }

      // Creating a new tab — cap enforcement lives here so the throw
      // lands inside the caller's try/catch.
      if (currentTabs.length >= MAX_TABS) {
        throw new TabCapError();
      }

      dispatch({ type: 'OPEN', path });
    },
    [dispatch],
  );

  // The "open a tab by path from anywhere" convenience. This is what every
  // opener OUTSIDE the tab strip should call (dropdowns, the command
  // palette, notification cards, in-page buttons). It converts the
  // cap-reached throw into the user-facing toast, so callers don't each
  // re-implement that.
  //
  // Crucially it goes through open() (focus-or-create), NOT react-router
  // navigate(). navigate() opens a tab only as a side effect of a URL
  // change, so navigating to the path you're already on is a no-op and no
  // tab opens. openTab() is idempotent and works regardless of the URL.
  const openTab = useCallback(
    (path, opts = {}) => {
      try {
        open(path, opts);
      } catch (err) {
        if (err && err.code === 'tabs.capReached') {
          emitTabCapReached();
          return;
        }
        throw err;
      }
    },
    [open],
  );

  const close = useCallback(
    (id) => dispatch({ type: 'CLOSE', id }),
    [dispatch],
  );

  const focus = useCallback(
    (id) => {
      // Validate synchronously so the throw lands inside the caller's
      // try/catch — same render-time hazard as OPEN.
      if (!tabsRef.current.some((t) => t.id === id)) {
        throw new Error(`tabs: focus(${id}) — id not in open tabs`);
      }
      dispatch({ type: 'FOCUS', id });
    },
    [dispatch],
  );

  const closeAllExceptHome = useCallback(() => {
    // Persist the home-only state to sessionStorage synchronously BEFORE
    // dispatching. The one caller today is the store switch, which runs
    // this as `switchStore`'s pre-restart teardown — the document is
    // replaced before React processes the dispatch queue, so the
    // useEffect-based save in TabsProvider doesn't run in time. Writing
    // here makes the new document pick up the correct state without
    // depending on render timing.
    const currentTabs = tabsRef.current;
    currentTabs
      .filter((t) => t.id !== HOME_TAB_ID)
      .forEach((t) => {
        clearTabSlots(t.id);
        markClean(t.id);
      });
    const home = currentTabs.find((t) => t.id === HOME_TAB_ID) || seedHomeTab();
    saveTabsState({ tabs: [home], activeId: HOME_TAB_ID });
    // Keep React state consistent for callers that DON'T replace the
    // document (CloseTabsOnStoreChange).
    dispatch({ type: 'CLOSE_ALL_EXCEPT_HOME' });
    // The surviving tab's path, for callers that replace the document
    // afterwards. The reload targets this path, not the current URL:
    // TabStrip's URL→activeId sync opens a tab for any path no open tab
    // matches, so reloading the current URL would reopen the tab just
    // closed.
    //
    // Read off the tab that was just persisted: loadTabsState re-pins the
    // restored home entry to HOME_TAB_PATH, so this IS the path the next
    // document's tab list will match.
    return home.path;
  }, [dispatch]);

  const setTitle = useCallback(
    (id, title) => dispatch({ type: 'SET_TITLE', id, title }),
    [dispatch],
  );

  // Drag-to-reorder. Policy lives here so the reducer stays a pure array
  // transform: Home is pinned at index 0 (non-closeable, always rendered
  // first), so it can neither be dragged nor be displaced by a drop before
  // it. Any reorder touching Home is a no-op.
  const reorderTabs = useCallback(
    (fromId, toId) => {
      if (fromId === HOME_TAB_ID || toId === HOME_TAB_ID) return;
      dispatch({ type: 'REORDER', fromId, toId });
    },
    [dispatch],
  );

  // Convenience for pages: set my own tab's title without needing the id.
  // Reads TabIdContext, which TabsHost sets around each rendered tab.
  const ownTabId = useContext(TabIdContext);
  const requireOwnTabId = useCallback(
    (caller) => {
      if (!ownTabId) throw new Error(`tabs: ${caller}() called outside a tab`);
      return ownTabId;
    },
    [ownTabId],
  );
  const setOwnTitle = useCallback(
    (title) => {
      dispatch({ type: 'SET_TITLE', id: requireOwnTabId('setOwnTitle'), title });
    },
    [dispatch, requireOwnTabId],
  );

  // "This tab is done — replace it with a tab at `path`": focus the open tab
  // at `path`, else create one, then close the calling tab. URL follows the
  // new activeId via TabsHost.
  //
  // FOCUS/OPEN is dispatched before CLOSE: a CLOSE first would focus the
  // left neighbor, and TabsHost would push that tab's URL. The tab count
  // never grows, so the cap does not apply.
  const replaceOwnTab = useCallback(
    (path) => {
      const ownId = requireOwnTabId('replaceOwnTab');
      const existing = tabsRef.current.find((t) => t.path === path);
      if (existing) {
        dispatch({ type: 'FOCUS', id: existing.id });
      } else {
        dispatch({ type: 'OPEN', path });
      }
      dispatch({ type: 'CLOSE', id: ownId });
    },
    [dispatch, requireOwnTabId],
  );

  const countOpen = tabs.length;

  return {
    tabs,
    activeId,
    open,
    openTab,
    close,
    focus,
    closeAllExceptHome,
    setTitle,
    setOwnTitle,
    replaceOwnTab,
    reorderTabs,
    countOpen,
    MAX_TABS,
  };
}
